import { createMainMenuNodeFromEntry } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/create-main-menu-node-from-entry';
import { sortMenuSectionItemsForDeeplinkHighlight } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/sort-menu-section-items';
import { EntryTypeEnum } from 'generated/graphql-gateway';
import { ILocalizedMenuEntry, IMainMenuNode, PosServiceMode } from 'state/unified-menu/types';
import { normalizeType } from 'state/unified-menu/utils';

/**
 * Top-level SECTION children of the generic OneMenu root — store-less catalog
 * sections used for unavailable-section recovery chrome.
 */
export function buildCatalogSectionNodes(
  entries: readonly ILocalizedMenuEntry[],
  rootMenuEntryId: string | null | undefined,
  posServiceMode: PosServiceMode = 'pickup'
): IMainMenuNode[] {
  if (!rootMenuEntryId || !entries.length) {
    return [];
  }

  const byId = new Map(entries.map(entry => [entry.id, entry]));
  const root = byId.get(rootMenuEntryId);
  if (!root?.options?.entries?.length) {
    return [];
  }

  const nodes: IMainMenuNode[] = [];
  for (const ref of root.options.entries) {
    const entry = byId.get(ref.entryId);
    if (!entry) {
      continue;
    }
    if (normalizeType(entry.type) !== normalizeType(EntryTypeEnum.SECTION)) {
      continue;
    }
    nodes.push(createMainMenuNodeFromEntry(entry, posServiceMode));
  }
  return nodes;
}

/**
 * Direct children of a catalog section (items/combos/pickers/nested sections)
 * without store availability filtering — used for no-store section preview.
 */
export function buildCatalogSectionProductNodes(
  entries: readonly ILocalizedMenuEntry[],
  sectionId: string,
  posServiceMode: PosServiceMode = 'pickup'
): { title: string; products: IMainMenuNode[] } | null {
  if (!sectionId || !entries.length) {
    return null;
  }

  const byId = new Map(entries.map(entry => [entry.id, entry]));
  const section = byId.get(sectionId);
  if (!section || normalizeType(section.type) !== normalizeType(EntryTypeEnum.SECTION)) {
    return null;
  }

  const products: IMainMenuNode[] = [];
  for (const ref of section.options?.entries ?? []) {
    const entry = byId.get(ref.entryId);
    if (!entry) {
      continue;
    }
    products.push(createMainMenuNodeFromEntry(entry, posServiceMode));
  }

  return {
    title: section.name?.locale ?? section.id,
    products,
  };
}

/**
 * Pins the deeplinked unavailable section first, then the remaining catalog
 * sections for “Other available sections” recovery.
 */
export function buildUnavailableSectionRecoveryData(
  catalogSections: IMainMenuNode[],
  unavailableSectionId: string
): { data: IMainMenuNode[]; deeplinkUnavailableSectionId: string } | null {
  if (!unavailableSectionId || catalogSections.length < 2) {
    return null;
  }

  const hasUnavailable = catalogSections.some(section => section.id === unavailableSectionId);
  if (!hasUnavailable) {
    return null;
  }

  const data = sortMenuSectionItemsForDeeplinkHighlight(catalogSections, unavailableSectionId);
  const hasOther = data.some(section => section.id !== unavailableSectionId);
  if (!hasOther) {
    return null;
  }

  return {
    data,
    deeplinkUnavailableSectionId: unavailableSectionId,
  };
}
